import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, TrendingUp, AlertTriangle, RefreshCw, ShieldCheck, Euro, PackageCheck, Mail as MailIcon, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const eur = (v) => `€ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

function KpiCard({ icon: Icon, label, value, hint, testid, tone = "primary" }) {
  const tones = {
    primary: "text-zen-primary bg-zen-primary/10",
    info: "text-zen-info bg-zen-info/10",
    alert: "text-zen-alert bg-zen-alert/10",
    ink: "text-zen-ink bg-zen-bg",
  };
  return (
    <div className="zen-card p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon size={18}/>
        </div>
        <div>
          <div className="text-xs text-zen-ink2 uppercase tracking-wider">{label}</div>
          <div className="font-heading text-2xl mt-0.5">{value}</div>
          {hint && <div className="text-xs text-zen-ink2 mt-0.5">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [ppSetup, setPpSetup] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users", { params: { q, limit: 50 } }),
      ]);
      setStats(s.data);
      setUsers(u.data.items || []);
    } catch (e) {
      toast.error(errMsg(e, "Errore admin"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doSearch = async (e) => {
    e.preventDefault();
    load();
  };

  const toggleAdmin = async (u) => {
    if (!window.confirm(`Impostare admin=${!u.is_admin} per ${u.email}?`)) return;
    try {
      await api.put(`/admin/users/${u.user_id}`, { is_admin: !u.is_admin });
      toast.success("Aggiornato");
      load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const setupPaypal = async () => {
    if (!window.confirm("Creare i piani PayPal (Monthly + Yearly) su Meta? Puoi eseguirlo una sola volta.")) return;
    setPpSetup(true);
    try {
      const { data } = await api.post("/paypal/admin/setup-plans");
      toast.success("Piani creati — copia i 3 ID in .env e riavvia il backend");
      alert(
        `Monthly ID: ${data.monthly_plan_id}\nYearly ID: ${data.yearly_plan_id}\nProduct ID: ${data.product_id}\n\n${data.note}`
      );
    } catch (e) { toast.error(errMsg(e, "Setup fallito")); }
    finally { setPpSetup(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="zen-label flex items-center gap-2"><ShieldCheck size={12}/> Amministrazione</span>
          <h1 className="mt-2 font-heading text-3xl">Dashboard Admin</h1>
          <p className="text-zen-ink2 mt-1">Metriche SaaS in tempo reale, gestione utenti e configurazioni.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="rounded-full">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`}/> Aggiorna
          </Button>
          <Button data-testid="admin-paypal-setup" onClick={setupPaypal} disabled={ppSetup} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
            {ppSetup ? "Configuro…" : "Setup piani PayPal"}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard testid="k-total-users" icon={Users} label="Utenti totali" value={stats.users.total}
              hint={`+${stats.users.new_last_30d} negli ultimi 30gg`} tone="primary"/>
            <KpiCard testid="k-mrr" icon={Euro} label="MRR stimato" value={eur(stats.revenue.mrr)}
              hint={`ARR ${eur(stats.revenue.arr)}`} tone="primary"/>
            <KpiCard testid="k-active" icon={TrendingUp} label="Utenti attivi" value={stats.users.active}
              hint={`Trial: ${stats.users.trial}`} tone="info"/>
            <KpiCard testid="k-churn" icon={AlertTriangle} label="Churn recente" value={stats.revenue.churn_recent}
              hint="Cancellati/scaduti" tone="alert"/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard testid="k-invoices" icon={PackageCheck} label="Fatture emesse" value={stats.usage.invoices} tone="ink"/>
            <KpiCard testid="k-clients" icon={Users} label="Clienti totali" value={stats.usage.clients} tone="ink"/>
            <KpiCard testid="k-email-conn" icon={MailIcon} label="Email collegate" value={stats.usage.email_connected}
              hint={`${stats.usage.projects} progetti`} tone="ink"/>
            <KpiCard testid="k-wa-conn" icon={MessageCircle} label="WhatsApp collegati" value={stats.usage.wa_connected}
              hint={`${stats.usage.quotes} preventivi`} tone="ink"/>
          </div>
        </>
      )}

      {/* Users table */}
      <div className="zen-card overflow-hidden">
        <div className="p-4 border-b border-zen-border flex items-center gap-3">
          <form onSubmit={doSearch} className="flex-1 flex gap-2">
            <Input
              data-testid="admin-search"
              placeholder="Cerca per email o nome…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit" variant="outline" className="rounded-full">Cerca</Button>
          </form>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zen-bg text-zen-ink2 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Email</th>
                <th className="text-left px-5 py-3 font-medium">Nome</th>
                <th className="text-left px-5 py-3 font-medium">Piano</th>
                <th className="text-left px-5 py-3 font-medium">Provider</th>
                <th className="text-left px-5 py-3 font-medium">Registrato</th>
                <th className="text-right px-5 py-3 font-medium">Dati</th>
                <th className="text-right px-5 py-3 font-medium">Admin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zen-border">
              {users.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-zen-ink2">Nessun utente trovato.</td></tr>
              ) : users.map(u => (
                <tr key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                  <td className="px-5 py-3 font-medium truncate max-w-[200px]">{u.email}</td>
                  <td className="px-5 py-3">{u.name}</td>
                  <td className="px-5 py-3">
                    <Badge variant="outline" className="rounded-full font-normal capitalize">{u.subscription_status}</Badge>
                  </td>
                  <td className="px-5 py-3 text-zen-ink2 text-xs">
                    {u.auth_provider} · {u.subscription_provider || "—"}
                  </td>
                  <td className="px-5 py-3 text-zen-ink2 text-xs">{fmtDate(u.created_at)}</td>
                  <td className="px-5 py-3 text-right text-xs text-zen-ink2">
                    {u._stats?.clients || 0} cli · {u._stats?.invoices || 0} fat · {u._stats?.projects || 0} prj
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => toggleAdmin(u)}
                      data-testid={`admin-toggle-${u.user_id}`}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs ${u.is_admin ? "bg-zen-primary/10 text-zen-primary border border-zen-primary/30" : "bg-zen-bg text-zen-ink2 border border-zen-border"}`}
                    >
                      {u.is_admin ? "Admin" : "Utente"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
